import { useEffect, useMemo, useRef, useState, memo, useCallback, startTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import TitleSectionBack from 'Containers/Common/TitleSectionBack';
import Modal from 'Containers/Common/Modal';
import ReviewsStatsCard from 'Containers/Reviews/containers/ReviewsStatsCard';
import { reviewsActions } from 'Containers/Reviews/store';
import { getReviewsStats } from 'Containers/Reviews/store/selectors';
import { getActiveSalon, getActiveSalonId } from 'Containers/Auth/store/selectors';
import { isDemoMode } from 'demo';
import { salonManagementActions } from 'Containers/SalonManagement/store';
import { getWorkers } from 'Containers/SalonManagement/store/selectors';
import { statisticActions } from 'Containers/Statistic/store';
import {
  getClients,
  getStatisticsBookings,
  getStatisticsFinance,
  getStatisticsLoading,
  getStatisticsLockVersion,
  getStatisticsQueue,
  getStatisticsUnlockedVersion,
} from 'Containers/Statistic/store/selectors';
import { modalActions } from 'Containers/Modals/store';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import useWindowSize from 'utils/useWindowSize';
import { clearStatsUnlock } from 'utils/statsUnlock';
import styles from './StatisticNew.module.scss';
import StatsFiltersBar, {
  formatPeriodLabel,
  getPresetLabel,
  getPresetRange,
} from './StatsFiltersBar';
import PremiumFeatureLock from './PremiumFeatureLock';
import StatsPasswordGate from './StatsPasswordGate';
import {
  COMPARE_OPTIONS,
  buildDisdetteDonutData,
  formatDecimal,
  formatEuro,
  safeDivide,
  COLOR_MAP,
  buildCategoryPoltronaTable,
  buildQueuePoltronaTable,
  buildDisdettePoltronaTable,
  getPoltronaColor,
  getPoltronaLegendItems,
  poltronaColorWithAlpha,
} from './mockData';
import {
  COMPARE_TO_API,
  EMPTY_CLIENTI_SCOPE,
  EMPTY_STATISTICS_SCOPE,
  detectQueueGroupBy,
  getCompareDeltaLabel,
  mapClientsToScope,
  mergeStatisticsScope,
  pctDelta,
} from './apiMappers';

dayjs.extend(isBetweenPlugin);

function sumTeamMetric(team, key) {
  return (team || []).reduce((sum, member) => sum + (Number(member?.[key]) || 0), 0);
}

const buildEntrateBreakdownFromTeam = (team) => {
  const items = (team || [])
    .map((member) => ({
      id: member.id,
      label: member.name,
      amount: Math.round(Number(member.fatturato) || 0),
    }))
    .filter((item) => item.amount > 0);
  const total = items.reduce((sum, item) => sum + item.amount, 0);
  if (!total) return items;

  const withPct = items.map((item) => ({
    ...item,
    pct: Math.round((item.amount / total) * 100),
  }));
  const diff = 100 - withPct.reduce((sum, item) => sum + item.pct, 0);
  if (diff && withPct.length) {
    const maxIdx = withPct.reduce(
      (best, item, idx) => (item.amount > withPct[best].amount ? idx : best),
      0,
    );
    withPct[maxIdx].pct = Math.max(0, withPct[maxIdx].pct + diff);
  }
  return withPct;
};

const formatCountPct = (pct, count) => {
  if (count == null || count === '') return `${pct}%`;
  return `${Math.round(Number(count) || 0)} · ${pct}%`;
};

const SUMMARY_PATTERN_CLASS = {
  queue: styles.summaryPattern_queue,
  recovered: styles.summaryPattern_recovered,
  daCliente: styles.summaryPattern_daCliente,
  daSalone: styles.summaryPattern_daSalone,
};

const getSummaryPatternClass = (key) => SUMMARY_PATTERN_CLASS[key];
const getSegmentColor = (segmentKey) => COLOR_MAP[segmentKey] || segmentKey;

const CHART_PATTERN_CLASS = {
  diagonal: styles.chartPatternDiagonal,
  vertical: styles.chartPatternVertical,
};

const polarPoint = (cx, cy, radius, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
};

const describeDonutSlice = (cx, cy, innerR, outerR, startPct, endPct) => {
  const startAngle = (startPct / 100) * 360;
  const endAngle = (endPct / 100) * 360;
  const sweep = endAngle - startAngle;

  if (sweep >= 359.99) {
    const midOuter = polarPoint(cx, cy, outerR, startAngle + 180);
    const midInner = polarPoint(cx, cy, innerR, startAngle + 180);
    const startOuter = polarPoint(cx, cy, outerR, startAngle);
    const startInner = polarPoint(cx, cy, innerR, startAngle);
    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${midOuter.x} ${midOuter.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${startOuter.x} ${startOuter.y}`,
      `L ${startInner.x} ${startInner.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${midInner.x} ${midInner.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${startInner.x} ${startInner.y}`,
      'Z',
    ].join(' ');
  }

  const largeArc = sweep > 180 ? 1 : 0;
  const startOuter = polarPoint(cx, cy, outerR, endAngle);
  const endOuter = polarPoint(cx, cy, outerR, startAngle);
  const startInner = polarPoint(cx, cy, innerR, startAngle);
  const endInner = polarPoint(cx, cy, innerR, endAngle);

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
};

const DONUT_CX = 50;
const DONUT_CY = 50;
const DONUT_INNER_RING_INNER = 24;
const DONUT_INNER_RING_OUTER = 36;
const DONUT_OUTER_RING_INNER = 36;
const DONUT_OUTER_RING_OUTER = 50;

const WaffleGrid = ({ ratePct, color }) => {
  const filled = Math.max(0, Math.min(100, Math.round(Number(ratePct) || 0)));

  return (
    <div className={styles.waffle} aria-hidden>
      {Array.from({ length: 100 }, (_, index) => (
        <i
          key={index}
          className={styles.waffleCell}
          style={{ background: index < filled ? color : undefined }}
        />
      ))}
    </div>
  );
};

const renderDonutRing = (slices, innerR, outerR, resolveFill) => {
  let acc = 0;

  return (slices || []).map((slice, index) => {
    const start = acc;
    acc += slice.pct;

    return (
      <path
        key={slice.id}
        d={describeDonutSlice(DONUT_CX, DONUT_CY, innerR, outerR, start, acc)}
        fill={resolveFill(slice, index)}
      />
    );
  });
};

const renderCategoryDividers = (innerSlices, innerR, outerR) => {
  const pctCliente = innerSlices.find((slice) => slice.id === 'daCliente')?.pct ?? 0;
  const pctSalone = innerSlices.find((slice) => slice.id === 'daSalone')?.pct ?? 0;

  if (pctCliente <= 0 || pctSalone <= 0) return null;

  return [0, pctCliente].map((startPct) => {
    const angleDeg = (startPct / 100) * 360;
    const innerPt = polarPoint(DONUT_CX, DONUT_CY, innerR, angleDeg);
    const outerPt = polarPoint(DONUT_CX, DONUT_CY, outerR, angleDeg);

    return (
      <line
        key={`divider-${startPct}`}
        x1={innerPt.x}
        y1={innerPt.y}
        x2={outerPt.x}
        y2={outerPt.y}
        className={styles.disdetteDonutDivider}
      />
    );
  });
};

const DisdetteDonut = ({ data, total, delta }) => {
  const inner = data?.inner || [];
  const outer = data?.outer || [];
  const hasData = inner.length > 0;

  if (!hasData) {
    return (
      <div className={styles.disdetteDonut}>
        <div className={styles.disdetteDonutHole}>
          <span className={clsx(styles.disdetteDonutTotal, styles.mono)}>0</span>
        </div>
      </div>
    );
  }

  const hasOuter = outer.length > 0;
  const innerOuterR = hasOuter ? DONUT_INNER_RING_OUTER : DONUT_OUTER_RING_OUTER;
  const dividerOuterR = hasOuter ? DONUT_OUTER_RING_OUTER : innerOuterR;
  const patternSlices = inner.filter((slice) => slice.pattern !== 'solid');

  return (
    <div className={styles.disdetteDonut}>
      <svg viewBox="0 0 100 100" className={styles.disdetteDonutSvg} aria-hidden>
        <defs>
          {patternSlices.map((slice, index) => (
            <pattern
              key={slice.id}
              id={`disdette-inner-pattern-${index}`}
              width="5"
              height="5"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="5" height="5" fill={slice.color} />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="5"
                stroke="rgba(0, 0, 0, 0.28)"
                strokeWidth="1.5"
              />
            </pattern>
          ))}
        </defs>
        <g>
          {renderDonutRing(inner, DONUT_INNER_RING_INNER, innerOuterR, (slice) => {
            if (slice.pattern === 'solid') return slice.color;
            const patternIndex = patternSlices.findIndex((item) => item.id === slice.id);
            return `url(#disdette-inner-pattern-${patternIndex})`;
          })}
        </g>
        {hasOuter && (
          <g className={styles.disdetteDonutOuterRing}>
            {renderDonutRing(
              outer,
              DONUT_OUTER_RING_INNER,
              DONUT_OUTER_RING_OUTER,
              (slice) => slice.color,
            )}
          </g>
        )}
        <g className={styles.disdetteDonutDividers}>
          {renderCategoryDividers(inner, DONUT_INNER_RING_INNER, dividerOuterR)}
        </g>
      </svg>
      <div className={styles.disdetteDonutHole}>
        <span className={clsx(styles.disdetteDonutTotal, styles.mono)}>{total}</span>
        <InlineDelta delta={delta} className={styles.disdetteDonutDelta} />
      </div>
    </div>
  );
};

const InlineDelta = ({ delta, className, positiveIsDown = false }) => {
  if (!delta || !Number(delta.pct)) return null;

  const isDown = delta.direction === 'down';
  const isPositive = positiveIsDown ? isDown : !isDown;

  return (
    <span className={clsx(styles.inlineDelta, className, isPositive ? styles.up : styles.down)}>
      {isDown ? '▼' : '▲'} {delta.pct}%
    </span>
  );
};

const DeltaBadge = ({ delta, deltaLabel, fallback = null, positiveIsDown = false, refreshing = false }) => {
  if (refreshing) {
    return (
      <div className={clsx(styles.delta, styles.deltaRefreshing)} aria-busy="true">
        <span className={styles.deltaMiniSpinner} />
        Aggiorno confronto…
      </div>
    );
  }

  if (!delta) {
    return fallback ? <div className={styles.metricHint}>{fallback}</div> : null;
  }

  const isDown = delta.direction === 'down';
  const isPositive = positiveIsDown ? isDown : !isDown;

  return (
    <div className={clsx(styles.delta, isPositive ? styles.up : styles.down)}>
      {isDown ? '▼' : '▲'} {Math.abs(delta.pct)}% {deltaLabel}
    </div>
  );
};

const FIN_MODAL_ACCENT = {
  entrate: '#02AD84',
  uscite: '#FCB734',
};

const FinBreakdownBars = ({ items, totalLabel, accentColor }) => {
  const total = (items || []).reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className={styles.finBreakdown}>
      <div className={styles.finBreakdownTotal}>
        <span>{totalLabel}</span>
        <span
          className={clsx(styles.mono, styles.finBreakdownTotalValue)}
          style={{ color: accentColor }}
        >
          {formatEuro(total)}
        </span>
      </div>
      <ul className={styles.finBreakdownBars}>
        {(items || []).map((item) => (
          <li key={item.id || item.label} className={styles.finBreakdownItem}>
            <div className={styles.categoryBarHead}>
              <span className={styles.categoryBarLabel}>{item.label}</span>
              <span className={clsx(styles.categoryBarPct, styles.mono)}>
                {formatEuro(item.amount)} · {item.pct}%
              </span>
            </div>
            <div className={styles.categoryBarTrack}>
              <div
                className={styles.categoryBarFill}
                style={{ width: `${item.pct}%`, backgroundColor: item.accentColor || accentColor }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SummaryTotalBar = ({
  items = [],
  className,
  children,
  legendBefore,
  showCategoryPct = false,
  showSummaryCategories = true,
  showCounts = false,
  segmentVariant = 'color',
}) => {
  const rows = Array.isArray(items) ? items : [];
  const totalCount = rows.reduce((sum, row) => sum + (Number(row?.[3]) || 0), 0);

  return (
  <div className={clsx(styles.categoryBarItem, styles.categorySummary, className)}>
    {legendBefore ? (
      <div className={styles.poltronaLegendAbove}>{legendBefore}</div>
    ) : null}
    <div className={styles.categoryBarHead}>
      <span className={styles.categoryBarLabel}>Totale</span>
      {showCounts ? (
        <span className={clsx(styles.categoryBarPct, styles.mono)}>{totalCount}</span>
      ) : null}
    </div>
    <div className={styles.categoryBarTrack}>
      <div className={styles.categoryBarFillGroup} style={{ width: '100%' }}>
        {rows.map(([label, pct, segmentKey, count]) => (
          <div
            key={label}
            className={clsx(
              styles.categoryBarSegment,
              segmentVariant === 'pattern' && getSummaryPatternClass(segmentKey),
            )}
            style={
              segmentVariant === 'color'
                ? { width: `${pct}%`, backgroundColor: getSegmentColor(segmentKey) }
                : { width: `${pct}%` }
            }
            title={`${label} ${showCounts ? formatCountPct(pct, count) : `${pct}%`}`}
          />
        ))}
      </div>
    </div>
    {showSummaryCategories && (
      <div className={styles.summaryCategories}>
        {rows.map(([label, pct, segmentKey]) => (
          <span key={label} className={styles.summaryCategoryItem}>
            {segmentVariant === 'pattern' ? (
              <i
                className={clsx(
                  styles.summaryPatternSwatch,
                  getSummaryPatternClass(segmentKey),
                )}
              />
            ) : (
              <i className={styles.poltronaDot} style={{ background: getSegmentColor(segmentKey) }} />
            )}
            {label}
            {!showCounts && showCategoryPct ? ` ${pct}%` : ''}
          </span>
        ))}
      </div>
    )}
    {children}
  </div>
  );
};

const DetailButton = ({ onClick, label = 'Dettaglio' }) => (
  <button type="button" className={styles.finDettaglio} onClick={onClick}>
    {label} <span className={styles.finDettaglioChev}>▾</span>
  </button>
);

const CardTitleRow = ({ title, showDetail, onDetailClick }) => (
  <div className={styles.cardTitleRow}>
    <h3 className={styles.cardTitle}>{title}</h3>
    {showDetail && <DetailButton onClick={onDetailClick} />}
  </div>
);

const PoltronaDetailTable = ({ columns = [], rows = [], cells = {} }) => {
  if (!rows?.length || !columns?.length) {
    return <p className={styles.metricHint}>Nessun dato disponibile per poltrona.</p>;
  }

  return (
    <>
      <p className={styles.poltronaTableScrollHint}>Scorri orizzontalmente per vedere tutte le colonne</p>
      <div className={styles.poltronaTableWrap}>
      <table className={styles.poltronaTable}>
        <thead>
          <tr>
            <th>Poltrona</th>
            {columns.map((col) => (
              <th key={col.key}>
                <span className={styles.poltronaTableColHead}>
                  <span>
                    {col.color && (
                      <i className={styles.poltronaTableDot} style={{ background: col.color }} />
                    )}
                    {col.label}
                  </span>
                  {col.subLabel && (
                    <span className={styles.poltronaTableSubLabel}>{col.subLabel}</span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row">{row.name}</th>
              {columns.map((col) => (
                <td key={col.key} className={styles.mono}>
                  {cells[row.id]?.[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
};

const PoltronaLegend = ({ items }) => (
  <div className={styles.poltronaLegend}>
    {(items || []).map(({ id, name, color }) => (
      <span
        key={id}
        className={styles.poltronaLegendAvatar}
        style={{ background: color }}
        title={name}
      >
        {getInitials(name)}
      </span>
    ))}
  </div>
);

const StackedVerticalBar = ({
  total,
  segments,
  showPoltronaSplit,
  barClassName,
  baseColor,
  poltronaList,
  patternType = 'solid',
  hideValue = false,
  empty = false,
}) => (
  <>
    {!hideValue && <span className={styles.barValue}>{total}</span>}
    {showPoltronaSplit && !empty && segments?.length > 0 ? (
      <div className={styles.stackedBarWrap}>
        <div className={styles.stackedBar}>
          {segments
            .filter((segment) => segment.sharePct > 0)
            .map((segment) => (
              <div
                key={segment.id}
                className={clsx(
                  styles.stackedBarSegment,
                  patternType !== 'solid' && CHART_PATTERN_CLASS[patternType],
                )}
                style={{
                  flex: Math.max(segment.sharePct, 1),
                  backgroundColor:
                    segment.color || getPoltronaColor(segment.id, poltronaList),
                }}
                title={`${segment.name} ${segment.sharePct}%`}
              />
            ))}
        </div>
      </div>
    ) : (
      <div
        className={clsx(
          styles.bar,
          barClassName,
          empty && styles.barEmpty,
          !empty && patternType !== 'solid' && CHART_PATTERN_CLASS[patternType],
        )}
        style={
          empty
            ? { borderColor: baseColor }
            : { backgroundColor: baseColor }
        }
      />
    )}
  </>
);

const QUEUE_GROUP_COPY = {
  day: { chart: 'per giorno', hint: 'giorno', avg: 'Media giornaliera' },
  weekday: { chart: 'per giorno', hint: 'giorno', avg: 'Media giornaliera' },
  week: { chart: 'per settimana', hint: 'settimana', avg: 'Media settimanale' },
  month: { chart: 'per mese', hint: 'mese', avg: 'Media mensile' },
};

const SoftLoad = ({ loading, className, children }) => (
  <div
    className={clsx(className, { [styles.softLoading]: loading })}
    aria-busy={loading || undefined}
  >
    {children}
  </div>
);

const SectionSkeleton = ({ variant = 'card' }) => (
  <div
    className={clsx(styles.sectionSkeleton, styles[`sectionSkeleton_${variant}`])}
    aria-busy="true"
    aria-label="loading"
  >
    {variant === 'strip' ? (
      <div className={styles.skelStrip}>
        {[
          { chip: 'dettaglio', value: 'lg', hint: 'wide' },
          { chip: 'spese', value: 'md', hint: 'mid' },
          { chip: null, value: 'sm', hint: 'long' },
        ].map((col, key) => (
          <div key={key} className={styles.skelStripCol} style={{ '--skel-delay': `${key * 0.08}s` }}>
            <div className={styles.skelStripHead}>
              <div className={clsx(styles.skelLine, styles.skelLabel)} />
              {col.chip === 'spese' ? (
                <div className={clsx(styles.skelLine, styles.skelChipLink)} />
              ) : null}
            </div>
            <div className={styles.skelStripValueRow}>
              <div
                className={clsx(
                  styles.skelLine,
                  styles.skelLineValue,
                  col.value === 'md' && styles.skelLineValueMd,
                  col.value === 'sm' && styles.skelLineValueTight,
                )}
              />
              {col.chip ? <div className={clsx(styles.skelLine, styles.skelChipLink)} /> : null}
            </div>
            <div className={clsx(styles.skelLine, styles.skelBadge)} />
            <div
              className={clsx(
                styles.skelLine,
                styles.skelHint,
                col.hint === 'mid' && styles.skelHintMid,
                col.hint === 'long' && styles.skelHintLong,
              )}
            />
          </div>
        ))}
      </div>
    ) : variant === 'metrics' ? (
      <div className={styles.skelMetrics}>
        {[
          { value: 'wide', hint: false },
          { value: 'mid', hint: true },
        ].map((col, key) => (
          <div key={key} className={styles.skelMetricCol} style={{ '--skel-delay': `${key * 0.08}s` }}>
            <div className={clsx(styles.skelLine, styles.skelLabel)} />
            <div className={styles.skelMetricValueRow}>
              <div
                className={clsx(
                  styles.skelLine,
                  styles.skelLineValue,
                  col.value === 'mid' && styles.skelLineValueMd,
                )}
              />
              <div className={clsx(styles.skelLine, styles.skelBadge)} />
            </div>
            {col.hint ? <div className={clsx(styles.skelLine, styles.skelHint)} /> : null}
          </div>
        ))}
      </div>
    ) : variant === 'bookings' ? (
      <div className={styles.skelBookings}>
        {[
          { bars: [78, 54, 36, 24] },
          { bars: [82, 48, 31] },
        ].map((panel, panelIdx) => (
          <div
            key={panelIdx}
            className={styles.skelBookingPanel}
            style={{ '--skel-delay': `${panelIdx * 0.08}s` }}
          >
            <div className={styles.skelBookingHead}>
              <div className={clsx(styles.skelLine, styles.skelTitle)} />
              <div className={clsx(styles.skelLine, styles.skelChip)} />
            </div>
            <div className={clsx(styles.skelLine, styles.skelLineValueSm)} />
            <div className={styles.skelCategoryList}>
              {panel.bars.map((width, idx) => (
                <div key={idx} className={styles.skelCategoryRow}>
                  <div className={styles.skelCategoryMeta}>
                    <div
                      className={clsx(styles.skelLine, styles.skelCatLabel)}
                      style={{ width: `${72 + idx * 12}px` }}
                    />
                    <div className={clsx(styles.skelLine, styles.skelCatPct)} />
                  </div>
                  <div className={styles.skelCategoryTrack}>
                    <span style={{ width: `${width}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {panelIdx === 0 ? <div className={clsx(styles.skelLine, styles.skelFootnote)} /> : null}
          </div>
        ))}
      </div>
    ) : variant === 'team' ? (
      <div className={styles.skelTeamRow}>
        {[0, 1, 2, 3].map((key) => (
          <div key={key} className={styles.skelTeamCard}>
            <div className={styles.skelTeamCardTop}>
              <div className={styles.skelAvatar} />
              <div className={styles.skelTeamCardText}>
                <div className={styles.skelLine} />
                <div className={clsx(styles.skelLine, styles.skelLineShort)} />
              </div>
            </div>
            <div className={styles.skelTeamStats}>
              <div className={clsx(styles.skelLine, styles.skelLineValueSm)} />
              <div className={clsx(styles.skelLine, styles.skelLineValueSm)} />
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        <div className={clsx(styles.skelLine, styles.skelTitle)} />
        <div className={clsx(styles.skelLine, styles.skelHint)} />
        <div className={styles.skelCategoryList}>
          {[68, 52, 40, 28].map((width, idx) => (
            <div key={idx} className={styles.skelCategoryRow}>
              <div className={styles.skelCategoryMeta}>
                <div className={clsx(styles.skelLine, styles.skelCatLabel)} />
                <div className={clsx(styles.skelLine, styles.skelCatPct)} />
              </div>
              <div className={styles.skelCategoryTrack}>
                <span style={{ width: `${width}%` }} />
              </div>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

const formatWaitlistHover = (row) => {
  if (!row) return '';
  return `${row.label} - recuperati ${row.recovered} su ${row.queue} in coda`;
};

const QueueChart = ({
  queue = [],
  queueMax,
  recoveredMax,
  hoveredIndex,
  onHover,
  groupBy = 'month',
  isMobile = false,
  detailAction = null,
}) => {
  const rows = queue || [];
  const qMax = Math.max(queueMax || 1, 1);
  const rMax = Math.max(recoveredMax || 1, 1);
  const copy = QUEUE_GROUP_COPY[groupBy] || QUEUE_GROUP_COPY.month;
  // Only scroll for dense day/week buckets; months should stretch full width
  const denseBuckets = groupBy === 'day' || groupBy === 'weekday' || groupBy === 'week';
  const colMinWidth = denseBuckets ? (groupBy === 'week' ? 44 : 36) : 48;
  const needsScroll = denseBuckets && rows.length > 8;
  const colStyle = needsScroll
    ? { flex: `0 0 ${colMinWidth}px`, minWidth: colMinWidth }
    : undefined;
  const scrollMinWidth = needsScroll ? rows.length * (colMinWidth + 3) : undefined;

  const queueScrollRef = useRef(null);
  const recoveredScrollRef = useRef(null);
  const syncingScroll = useRef(false);

  const syncScroll = useCallback((source) => {
    if (syncingScroll.current || !needsScroll) return;
    const from = source === 'queue' ? queueScrollRef.current : recoveredScrollRef.current;
    const to = source === 'queue' ? recoveredScrollRef.current : queueScrollRef.current;
    if (!from || !to) return;
    syncingScroll.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => {
      syncingScroll.current = false;
    });
  }, [needsScroll]);

  const selectBucket = (index) => {
    if (isMobile) {
      onHover(hoveredIndex === index ? null : index);
      return;
    }
    onHover(index);
  };

  const colClass = (index) =>
    clsx(styles.waitlistCol, {
      [styles.hovered]: hoveredIndex === index,
      [styles.dimmed]: hoveredIndex != null && hoveredIndex !== index,
    });

  return (
    <div
      className={styles.waitlistCharts}
      onMouseLeave={isMobile ? undefined : () => onHover(null)}
    >
      {detailAction ? <div className={styles.waitlistDetailRow}>{detailAction}</div> : null}
      <div className={styles.waitlistChartHead}>
        <span className={styles.waitlistLegend}>
          <i style={{ background: COLOR_MAP.queue }} />
          Clienti in lista d&apos;attesa, {copy.chart}
        </span>
        <span className={styles.waitlistHint}>
          {isMobile ? 'Tocca la colonna per il dettaglio' : 'Passa sulla colonna per il dettaglio'}
        </span>
      </div>

      <div
        ref={queueScrollRef}
        className={clsx(styles.waitlistScroll, needsScroll && styles.waitlistScrollActive)}
        onScroll={() => syncScroll('queue')}
      >
        <div className={styles.waitlistScrollInner} style={scrollMinWidth ? { minWidth: scrollMinWidth } : undefined}>
          <div className={styles.waitlistBars} style={{ '--waitlist-h': '180px' }}>
            {rows.map((row, index) => {
              const h = row.queue > 0 ? Math.max((row.queue / qMax) * 180, 4) : 2;
              return (
                <div
                  key={`q-${row.label}`}
                  className={colClass(index)}
                  style={colStyle}
                  onMouseEnter={isMobile ? undefined : () => onHover(index)}
                  onClick={() => selectBucket(index)}
                >
                  <span className={clsx(styles.waitlistVal, styles.mono)}>{row.queue}</span>
                  <div
                    className={styles.waitlistBar}
                    style={{ height: `${h}px`, background: COLOR_MAP.queue }}
                  />
                </div>
              );
            })}
          </div>

          <div className={styles.waitlistLabels}>
            {rows.map((row, index) => (
              <span
                key={`ql-${row.label}`}
                className={clsx({ [styles.hovered]: hoveredIndex === index })}
                style={colStyle}
                onMouseEnter={isMobile ? undefined : () => onHover(index)}
                onClick={() => selectBucket(index)}
              >
                {row.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={clsx(styles.waitlistChartHead, styles.waitlistChartHeadSecondary)}>
        <span className={styles.waitlistLegend}>
          <i style={{ background: COLOR_MAP.recovered }} />
          Recuperati, entrati in agenda
        </span>
        <span className={styles.waitlistHint}>
          Scala propria — i valori sono di un ordine di grandezza inferiore
        </span>
      </div>

      <div
        ref={recoveredScrollRef}
        className={clsx(styles.waitlistScroll, needsScroll && styles.waitlistScrollActive)}
        onScroll={() => syncScroll('recovered')}
      >
        <div className={styles.waitlistScrollInner} style={scrollMinWidth ? { minWidth: scrollMinWidth } : undefined}>
          <div className={styles.waitlistBars} style={{ '--waitlist-h': '46px' }}>
            {rows.map((row, index) => {
              const h = row.recovered > 0 ? Math.max((row.recovered / rMax) * 46, 4) : 2;
              return (
                <div
                  key={`r-${row.label}`}
                  className={colClass(index)}
                  style={colStyle}
                  onMouseEnter={isMobile ? undefined : () => onHover(index)}
                  onClick={() => selectBucket(index)}
                >
                  <span
                    className={clsx(styles.waitlistVal, styles.mono)}
                    style={{ color: row.recovered > 0 ? '#8a6420' : undefined }}
                  >
                    {row.recovered}
                  </span>
                  <div
                    className={styles.waitlistBar}
                    style={{
                      height: `${h}px`,
                      background: row.recovered > 0 ? COLOR_MAP.recovered : '#e4e1db',
                    }}
                  />
                </div>
              );
            })}
          </div>

          <div className={clsx(styles.waitlistLabels, styles.waitlistRates)}>
            {rows.map((row, index) => {
              const rate =
                row.queue > 0
                  ? `${formatDecimal(safeDivide(row.recovered, row.queue) * 100, 1)}%`
                  : '—';
              return (
                <span
                  key={`rl-${row.label}`}
                  className={clsx(styles.waitlistRecRate, {
                    [styles.hovered]: hoveredIndex === index,
                  })}
                  style={colStyle}
                  onMouseEnter={isMobile ? undefined : () => onHover(index)}
                  onClick={() => selectBucket(index)}
                >
                  {rate}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const CategoryBars = ({ items = [], deltas }) => (
  <div className={styles.categoryBarsWrap}>
    <ul className={styles.categoryBars}>
      {(items || []).map(([label, pct, colorKey, count]) => {
        const categoryColor = COLOR_MAP[colorKey];
        const countVal = count != null ? Math.round(Number(count) || 0) : null;
        const pctVal = Number(pct) || 0;
        const meta =
          countVal != null
            ? `${countVal.toLocaleString('it-IT')}  ·  ${pctVal}%`
            : `${pctVal}%`;

        return (
          <li key={label} className={styles.categoryBarItem}>
            <div className={styles.categoryBarHead}>
              <span className={styles.categoryBarLabel}>{label}</span>
              <span className={clsx(styles.categoryBarPct, styles.mono)}>
                <InlineDelta delta={deltas?.[label]} />
                {meta}
              </span>
            </div>
            <div className={styles.categoryBarTrack}>
              <div
                className={styles.categoryBarFill}
                style={{ width: `${pctVal}%`, backgroundColor: categoryColor }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

const getInitials = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/** Mock inventati: mai usare scope.uscite / utile / fatturato team reali quando il piano è Base. */
const MOCK_USCITE = {
  amount: 2840,
  delta: { direction: 'up', pct: 8 },
  deltaLabel: 'vs periodo prec.',
  hint: 'Spese registrate nel periodo.',
};

const MOCK_UTILE = {
  amount: 6120,
  delta: { direction: 'up', pct: 12 },
  deltaLabel: 'vs periodo prec.',
  hint: 'Differenza tra entrate e uscite.',
};

const FAKE_TEAM_STATS = [
  {
    fatturato: 4280,
    prenotazioni: 52,
    rank: 1,
    sharePct: 41.2,
    avg: 82.3,
    gapNote: 'Incassa più di tutti',
    color: '#6B70F5',
  },
  {
    fatturato: 3650,
    prenotazioni: 47,
    rank: 2,
    sharePct: 35.1,
    avg: 77.7,
    gapNote: '€630 meno del primo',
    color: '#FF8A3D',
  },
  {
    fatturato: 2460,
    prenotazioni: 31,
    rank: 3,
    sharePct: 23.7,
    avg: 79.4,
    gapNote: '€1.820 meno del primo',
    color: '#02AD84',
  },
];

const MOCK_TEAM_FALLBACK = [
  { id: 'mock-a', name: 'Alex Bianchi' },
  { id: 'mock-b', name: 'Giulia Verdi' },
  { id: 'mock-c', name: 'Marco Neri' },
];

const StatisticNew = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const activeSalonId = useSelector(getActiveSalonId());
  const activeSalon = useSelector(getActiveSalon());
  const passwordRequired = Boolean(activeSalon?.passwordStatistiche) && !isDemoMode(activeSalon);
  const demoMode = isDemoMode(activeSalon);
  const lockVersion = useSelector(getStatisticsLockVersion());
  const unlockedVersion = useSelector(getStatisticsUnlockedVersion());
  const unlockedBaselineRef = useRef(unlockedVersion);
  const skipCacheAfterUnlockRef = useRef(false);
  // Unlock solo per la visita corrente alla pagina Statistiche.
  const [statsUnlocked, setStatsUnlocked] = useState(() => !passwordRequired);
  const isStatsLocked = passwordRequired && !statsUnlocked;
  const canFetchLiveStats = !isStatsLocked;
  const reviewsStats = useSelector(getReviewsStats());
  const workers = useSelector(getWorkers()) || [];
  const financeData = useSelector(getStatisticsFinance());
  const bookingsData = useSelector(getStatisticsBookings());
  const queueData = useSelector(getStatisticsQueue());
  const clientsData = useSelector(getClients());
  const sectionLoading = useSelector(getStatisticsLoading()) || {};
  // Gate UX front-only: idealmente il backend non espone uscite/team ai piani Base.
  const isPremiumUnlocked = useIsPremiumUnlocked();
  const [poltrona, setPoltrona] = useState('tutte');
  const [periodPreset, setPeriodPreset] = useState('today');
  const [datePeriod, setDatePeriod] = useState(() => getPresetRange('today'));
  const [compare, setCompare] = useState(COMPARE_OPTIONS[0]);
  const [finModal, setFinModal] = useState(null);
  const [poltronaModal, setPoltronaModal] = useState(null);
  const [queueHoverIndex, setQueueHoverIndex] = useState(null);
  const [compareRefreshing, setCompareRefreshing] = useState(false);
  const teamCarouselRef = useRef(null);
  const teamCardRefs = useRef({});
  const loadedPeriodRef = useRef(null);
  const activeSalonIdRef = useRef(activeSalonId);

  useEffect(() => {
    activeSalonIdRef.current = activeSalonId;
  }, [activeSalonId]);

  const dataInizio = datePeriod?.start?.isValid?.()
    ? datePeriod.start.format('YYYY-MM-DD')
    : null;
  const dataFine = datePeriod?.end?.isValid?.()
    ? datePeriod.end.format('YYYY-MM-DD')
    : null;
  const periodKey = dataInizio && dataFine ? `${dataInizio}|${dataFine}` : null;
  const loadedKey =
    activeSalonId && periodKey ? `${activeSalonId}|${periodKey}` : null;
  const periodLabel =
    periodPreset === 'custom' ? formatPeriodLabel(datePeriod) : getPresetLabel(periodPreset);
  const compareKey = COMPARE_TO_API[compare] || 'none';
  const scope = useMemo(
    () => ({
      ...mergeStatisticsScope(financeData, bookingsData, queueData, {
        compareKey,
        poltronaId: poltrona,
      }),
      deltaLabel: getCompareDeltaLabel(compareKey),
    }),
    [financeData, bookingsData, queueData, compareKey, poltrona],
  );
  const clientiScope = useMemo(
    () => mapClientsToScope(clientsData, poltrona) || EMPTY_CLIENTI_SCOPE,
    [clientsData, poltrona],
  );
  const hardSectionLoading = {
    finance: Boolean(sectionLoading.finance) && !financeData,
    bookings: Boolean(sectionLoading.bookings) && !bookingsData,
    queue: Boolean(sectionLoading.queue) && !queueData,
    clients: Boolean(sectionLoading.clients) && !clientsData,
  };
  const softSectionLoading = {
    finance: Boolean(sectionLoading.finance) && !!financeData,
    bookings: Boolean(sectionLoading.bookings) && !!bookingsData,
    queue: Boolean(sectionLoading.queue) && !!queueData,
    clients: Boolean(sectionLoading.clients) && !!clientsData,
  };

  const poltronaOptions = useMemo(
    () => [
      { value: 'tutte', label: 'Tutte le poltrone' },
      ...(workers || [])
        .filter((worker) => !worker.disabilitato)
        .map((worker) => ({
          value: String(worker.id),
          label: worker.name,
        })),
    ],
    [workers],
  );

  useEffect(() => {
    loadedPeriodRef.current = null;
    setPoltrona('tutte');
    setCompareRefreshing(false);
    setFinModal(null);
    setPoltronaModal(null);
    setQueueHoverIndex(null);
    if (passwordRequired) {
      clearStatsUnlock(activeSalonId);
      setStatsUnlocked(false);
    } else {
      setStatsUnlocked(true);
    }
  }, [activeSalonId, passwordRequired, demoMode]);

  // Uscendo dalla route Statistiche si invalida l'unlock (Home → Statistiche = nuova password).
  useEffect(() => {
    return () => {
      clearStatsUnlock(activeSalonIdRef.current);
      dispatch(statisticActions.STATISTICS_SESSION_RESET.SUCCESS());
    };
  }, [dispatch]);

  useEffect(() => {
    if (lockVersion > 0) {
      loadedPeriodRef.current = null;
      setStatsUnlocked(false);
    }
  }, [lockVersion]);

  useEffect(() => {
    if (unlockedVersion > unlockedBaselineRef.current) {
      loadedPeriodRef.current = null;
      skipCacheAfterUnlockRef.current = true;
      unlockedBaselineRef.current = unlockedVersion;
      setStatsUnlocked(true);
    }
  }, [unlockedVersion]);

  useEffect(() => {
    if (!activeSalonId) return;
    dispatch(salonManagementActions.GET_WORKERS.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch, activeSalonId]);

  useEffect(() => {
    if (!activeSalonId || !dataInizio || !dataFine || !periodKey || !loadedKey) {
      return;
    }

    const compareApi = COMPARE_TO_API[compare] || 'none';

    if (isStatsLocked) {
      return;
    }

    // Periodo già caricato per QUESTO salone + nessun confronto → solo FE, niente refetch.
    if (loadedPeriodRef.current === loadedKey && compareApi === 'none') {
      setCompareRefreshing(false);
      return;
    }

    const softCompare = loadedPeriodRef.current === loadedKey;
    const skipSectionLoader = softCompare || Boolean(financeData);
    const skipCache = skipCacheAfterUnlockRef.current;
    setCompareRefreshing(Boolean(financeData) || softCompare);

    dispatch(
      statisticActions.GET_STATISTICS_FINANCE.REQUEST(
        {
          period: 'custom',
          compare: compareApi,
          poltrone: 'all',
          dataInizio,
          dataFine,
        },
        null,
        { skipLoader: skipSectionLoader, skipCache },
      ),
    );
  }, [
    dispatch,
    isStatsLocked,
    activeSalonId,
    dataInizio,
    dataFine,
    compare,
    periodKey,
    loadedKey,
  ]);

  useEffect(() => {
    if (isStatsLocked || !activeSalonId || !dataInizio || !dataFine || !periodKey || !loadedKey) {
      return;
    }

    const compareApi = COMPARE_TO_API[compare] || 'none';
    if (loadedPeriodRef.current === loadedKey && compareApi === 'none') {
      return;
    }

    const softCompare = loadedPeriodRef.current === loadedKey;
    const skipSectionLoader = softCompare || Boolean(bookingsData);
    const sectionPayload = {
      period: 'custom',
      compare: compareApi,
      poltrone: 'all',
      dataInizio,
      dataFine,
    };

    const skipCache = skipCacheAfterUnlockRef.current;
    dispatch(
      statisticActions.GET_STATISTICS_BOOKINGS.REQUEST(sectionPayload, null, {
        skipLoader: skipSectionLoader,
        skipCache,
      }),
    );
    dispatch(
      statisticActions.GET_STATISTICS_QUEUE.REQUEST(sectionPayload, null, {
        skipLoader: softCompare || Boolean(queueData),
        skipCache,
      }),
    );
  }, [
    dispatch,
    isStatsLocked,
    activeSalonId,
    dataInizio,
    dataFine,
    compare,
    periodKey,
    loadedKey,
  ]);

  useEffect(() => {
    if (!activeSalonId) return;

    dispatch(
      reviewsActions.GET_REVIEWS.REQUEST({}, null, { skipLoader: true }),
    );
  }, [dispatch, activeSalonId]);

  useEffect(() => {
    if (isStatsLocked || !activeSalonId || !dataInizio || !dataFine) return;

    dispatch(
      statisticActions.GET_STATISTICS_CLIENTS.REQUEST(
        {
          period: 'custom',
          poltrone: 'all',
          dataInizio,
          dataFine,
        },
        null,
        { skipLoader: Boolean(clientsData), skipCache: skipCacheAfterUnlockRef.current },
      ),
    );
  }, [dispatch, isStatsLocked, activeSalonId, dataInizio, dataFine]);

  useEffect(() => {
    if (!canFetchLiveStats || !loadedKey) return;
    if (sectionLoading.finance || sectionLoading.bookings || sectionLoading.queue) return;
    if (!financeData) return;
    loadedPeriodRef.current = loadedKey;
    skipCacheAfterUnlockRef.current = false;
    setCompareRefreshing(false);
  }, [
    canFetchLiveStats,
    loadedKey,
    sectionLoading.finance,
    sectionLoading.bookings,
    sectionLoading.queue,
    financeData,
  ]);

  useEffect(() => {
    setQueueHoverIndex(null);
  }, [dataInizio, dataFine, poltrona]);

  useEffect(() => {
    if (poltrona === 'tutte') return undefined;

    const carousel = teamCarouselRef.current;
    const cardEl = teamCardRefs.current[poltrona];
    if (!carousel || !cardEl) return undefined;

    const frame = requestAnimationFrame(() => {
      const carouselRect = carousel.getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();
      const delta =
        cardRect.left + cardRect.width / 2 - (carouselRect.left + carouselRect.width / 2);

      carousel.scrollBy({ left: delta, behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(frame);
  }, [poltrona, scope.team]);

  const entrateTotale = Number(scope.entrate) || 0;
  const usciteTotale = Number(scope.uscite) || 0;
  const utile = Number(scope.utile) || entrateTotale - usciteTotale;
  const entrateDelta = scope.deltas?.entrate;
  const entrateBreakdown =
    scope.finEntrateBreakdown?.length > 0
      ? scope.finEntrateBreakdown
      : buildEntrateBreakdownFromTeam(scope.team);
  const selectedTeamMember =
    poltrona === 'tutte'
      ? null
      : (scope.team || []).find((member) => String(member.id) === String(poltrona)) || null;
  const fatturatoAnalisi = selectedTeamMember
    ? Number(selectedTeamMember.fatturato) || 0
    : sumTeamMetric(scope.team, 'fatturato') || scope.fatturato;
  const prenotazioniAnalisi = selectedTeamMember
    ? Number(selectedTeamMember.prenotazioni) || 0
    : sumTeamMetric(scope.team, 'prenotazioni') || scope.prenotazioni;
  const scontrinoMedio = safeDivide(fatturatoAnalisi, prenotazioniAnalisi);
  const fatturatoDelta = scope.teamPrev
    ? pctDelta(
        fatturatoAnalisi,
        selectedTeamMember
          ? Number(
              (scope.teamPrev.find((member) => String(member.id) === String(poltrona)) || {})
                .fatturato,
            ) || 0
          : sumTeamMetric(scope.teamPrev, 'fatturato'),
      )
    : selectedTeamMember
      ? null
      : scope.deltas?.fatturato;
  const scontrinoMedioDelta = scope.teamPrev
    ? pctDelta(
        scontrinoMedio,
        safeDivide(
          selectedTeamMember
            ? Number(
                (scope.teamPrev.find((member) => String(member.id) === String(poltrona)) || {})
                  .fatturato,
              ) || 0
            : sumTeamMetric(scope.teamPrev, 'fatturato'),
          selectedTeamMember
            ? Number(
                (scope.teamPrev.find((member) => String(member.id) === String(poltrona)) || {})
                  .prenotazioni,
              ) || 0
            : sumTeamMetric(scope.teamPrev, 'prenotazioni'),
        ),
      )
    : selectedTeamMember
      ? null
      : scope.deltas?.scontrinoMedio;
  const clientiPericolosiPct =
    safeDivide(clientiScope.clientiPericolosiCount, clientiScope.clientiAttivi) * 100;
  const frequenzaVisita = safeDivide(clientiScope.prenotazioni, clientiScope.clientiAttivi);
  const scontrinoMedioCliente = safeDivide(clientiScope.fatturato, clientiScope.clientiAttivi);
  const clientiTotali = Number(clientiScope.clientiTotali) || 0;
  const clientiAttiviPeriodo = Number(clientiScope.clientiAttivi) || 0;
  // Barra: clientiAttivi (= prenotati nel periodo) + rischio (30–180gg) + resto.
  // Non sottrarre rischio da attivi: sono insiemi disgiunti.
  const clientiRecenti = clientiAttiviPeriodo;
  const clientiRischioBar = Number(clientiScope.clientiRischioAbbandono) || 0;
  const clientiDormienti = Math.max(0, clientiTotali - clientiRecenti - clientiRischioBar);
  const clientiAttiviPct = safeDivide(clientiAttiviPeriodo, clientiTotali) * 100;
  const isUltimoMesePeriod = useMemo(() => {
    if (!datePeriod?.start?.isValid?.() || !datePeriod?.end?.isValid?.()) return false;
    const today = dayjs().startOf('day');
    const end = datePeriod.end.startOf('day');
    const start = datePeriod.start.startOf('day');
    if (!end.isSame(today, 'day')) return false;
    const monthStart = today.startOf('month');
    const rollingStart = today.subtract(1, 'month').add(1, 'day');
    return start.isSame(monthStart, 'day') || start.isSame(rollingStart, 'day');
  }, [datePeriod]);
  const clientiRecentiLabel = isUltimoMesePeriod
    ? 'visti nell\'ultimo mese'
    : 'prenotati nel periodo';
  const clientiBarPct = (count) =>
    clientiTotali > 0 ? `${((count / clientiTotali) * 100).toFixed(1)}%` : '0%';

  const showPoltronaSplit = poltrona === 'tutte';

  const poltronaLegendItems = useMemo(
    () => getPoltronaLegendItems(scope.team),
    [scope.team],
  );

  const poltronaModalTable = useMemo(() => {
    if (!poltronaModal) return null;

    switch (poltronaModal) {
      case 'canali':
        return {
          title: 'Dettaglio canali per poltrona',
          ...buildCategoryPoltronaTable(scope.canali, scope.canaliSegments),
        };
      case 'servizi':
        return {
          title: 'Dettaglio servizi per poltrona',
          ...buildCategoryPoltronaTable(scope.servizi, scope.serviziSegments),
        };
      case 'queue':
        return {
          title: 'Recuperati dalla coda',
          ...buildQueuePoltronaTable(scope.queue),
        };
      case 'disdette':
        return {
          title: 'Dettaglio disdette per poltrona',
          ...buildDisdettePoltronaTable(scope.disdetteSegments),
        };
      default:
        return null;
    }
  }, [poltronaModal, scope]);

  const queueMax = useMemo(
    () => Math.max(...(scope.queue || []).map((d) => d.queue || 0), 1),
    [scope.queue],
  );
  const recoveredMax = useMemo(
    () => Math.max(...(scope.queue || []).map((d) => d.recovered || 0), 1),
    [scope.queue],
  );
  const canaliTotal = useMemo(
    () =>
      (scope.canali || []).reduce(
        (sum, row) => sum + (Array.isArray(row) ? Number(row[3] ?? 0) || 0 : 0),
        0,
      ),
    [scope.canali],
  );
  const serviziTotal = useMemo(
    () =>
      (scope.servizi || []).reduce(
        (sum, row) => sum + (Array.isArray(row) ? Number(row[3] ?? row[1]) || 0 : 0),
        0,
      ),
    [scope.servizi],
  );
  const teamRanked = useMemo(() => {
    if (!isPremiumUnlocked) return [];
    const list = [...(scope.team || [])].sort(
      (a, b) => (Number(b.fatturato) || 0) - (Number(a.fatturato) || 0),
    );
    const leaderFat = Number(list[0]?.fatturato) || 0;
    const fatTotal = list.reduce((sum, m) => sum + (Number(m.fatturato) || 0), 0) || 1;
    return list.map((member, index) => ({
      ...member,
      rank: index + 1,
      sharePct: ((Number(member.fatturato) || 0) / fatTotal) * 100,
      gapNote:
        index === 0
          ? 'Incassa più di tutti'
          : `${formatEuro(leaderFat - (Number(member.fatturato) || 0))} meno del primo`,
      avg: safeDivide(member.fatturato, member.prenotazioni),
    }));
  }, [scope.team, isPremiumUnlocked]);
  const lockedTeamPreview = useMemo(
    () =>
      FAKE_TEAM_STATS.map((fake, index) => {
        const persona = MOCK_TEAM_FALLBACK[index % MOCK_TEAM_FALLBACK.length];
        return {
          id: persona.id,
          name: persona.name,
          color: fake.color,
          fatturato: fake.fatturato,
          prenotazioni: fake.prenotazioni,
          rank: fake.rank,
          sharePct: fake.sharePct,
          avg: fake.avg,
          gapNote: fake.gapNote,
        };
      }),
    [],
  );
  const ricTotale = Number(scope.ricorrenti?.totale) || 0;
  const ricMonths = Math.max(1, Number(scope.ricorrenti?.monthsInPeriod) || 1);
  const ricSharePct = prenotazioniAnalisi
    ? Math.round((ricTotale / prenotazioniAnalisi) * 1000) / 10
    : 0;
  const ricValue = Math.round(ricTotale * (Number(scontrinoMedio) || 0));
  const ricMonthly = Math.round(ricTotale / ricMonths);
  const prenotazioniTotaleDisdette = Number(scope.disdette?.prenotazioniTotale) || 0;
  const disClienteRate = prenotazioniTotaleDisdette
    ? ((Number(scope.disdette?.daCliente) || 0) / prenotazioniTotaleDisdette) * 100
    : 0;
  const disSaloneRate = prenotazioniTotaleDisdette
    ? ((Number(scope.disdette?.daSalone) || 0) / prenotazioniTotaleDisdette) * 100
    : 0;
  const codaSum = Number(scope.queueSummary?.totalQueue) || 0;
  const recSum = Number(scope.queueSummary?.totalRecovered) || 0;
  const hoveredQueueRow =
    queueHoverIndex != null ? (scope.queue || [])[queueHoverIndex] : null;
  const queueGroupBy = detectQueueGroupBy(dataInizio, dataFine);
  const queueAvgCopy = QUEUE_GROUP_COPY[queueGroupBy] || QUEUE_GROUP_COPY.month;
  const giorniMax = useMemo(
    () => Math.max(...(clientiScope.giorni || []).map((d) => d.count), 1),
    [clientiScope.giorni],
  );
  const giorniMin = useMemo(() => {
    const counts = (clientiScope.giorni || []).map((d) => d.count);
    return counts.length ? Math.min(...counts) : 0;
  }, [clientiScope.giorni]);
  const bestDay = useMemo(() => {
    const days = clientiScope.giorni || [];
    if (!days.length) return null;
    return days.reduce((best, day) => (day.count > (best?.count || 0) ? day : best), days[0]);
  }, [clientiScope.giorni]);

  const goToSpese = () => {
    navigate(`${ROUTES.SETTINGS}${ROUTES.EXPENSES}`);
  };

  const goToUpgrade = () => {
    navigate(PREMIUM_UPGRADE_PATH);
  };

  const goToReviews = () => {
    navigate(ROUTES.REVIEWS);
  };

  const poltronaLabel =
    poltronaOptions.find((opt) => opt.value === poltrona)?.label || 'Tutte le poltrone';

  const handlePeriodChange = useCallback(({ period, preset }) => {
    startTransition(() => {
      setDatePeriod(period);
      setPeriodPreset(preset);
    });
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <TitleSectionBack
          title="Statistiche"
          description="Panoramica del salone"
        />
      </div>
      {isStatsLocked ? (
        <div className={styles.passwordOverlay}>
          <StatsPasswordGate />
        </div>
      ) : null}
      <div
        className={clsx(styles.pageBody, {
          [styles.pageBody_locked]: isStatsLocked,
        })}
      >
      <StatsFiltersBar
        poltrona={poltrona}
        poltronaOptions={poltronaOptions}
        poltronaLabel={poltronaLabel}
        onPoltronaChange={setPoltrona}
        periodPreset={periodPreset}
        periodLabel={periodLabel}
        onPeriodChange={handlePeriodChange}
        compare={compare}
        onCompareChange={setCompare}
        compareRefreshing={compareRefreshing}
      />

      <SoftLoad loading={softSectionLoading.finance} className={styles.finDashboard}>
        {hardSectionLoading.finance ? (
          <SectionSkeleton variant="strip" />
        ) : (
          <>
            <div className={clsx(styles.finItem, styles.entrate)}>
              <div className={styles.label}>Entrate</div>
              <div className={styles.finValueRow}>
                <div className={clsx(styles.value, styles.mono)}>{formatEuro(entrateTotale)}</div>
                <button type="button" className={styles.finDettaglio} onClick={() => setFinModal('entrate')}>
                  Dettaglio <span className={styles.finDettaglioChev}>▾</span>
                </button>
              </div>
              <DeltaBadge
                delta={entrateDelta}
                deltaLabel={scope.deltaLabel}
                refreshing={compareRefreshing}
              />
              <div className={styles.metricHint}>Quello che è entrato in cassa nel periodo.</div>
            </div>
            <div
              className={clsx(
                styles.finItem,
                styles.uscite,
                !isPremiumUnlocked && styles.finItemLocked,
              )}
            >
              {isPremiumUnlocked ? (
                <>
                  <div className={styles.finItemHeader}>
                    <div className={styles.label}>Uscite</div>
                    <button type="button" className={styles.vediSpese} onClick={goToSpese}>
                      Vedi spese <span className={styles.vediSpeseChev}>&gt;</span>
                    </button>
                  </div>
                  <div className={styles.finValueRow}>
                    <div className={clsx(styles.value, styles.mono)}>{formatEuro(usciteTotale)}</div>
                    <button
                      type="button"
                      className={styles.finDettaglio}
                      onClick={() => setFinModal('uscite')}
                    >
                      Dettaglio <span className={styles.finDettaglioChev}>▾</span>
                    </button>
                  </div>
                  <DeltaBadge
                    delta={scope.deltas?.uscite}
                    deltaLabel={scope.deltaLabel}
                    positiveIsDown
                    refreshing={compareRefreshing}
                  />
                  <div className={styles.metricHint}>
                    {usciteTotale > 0
                      ? 'Spese registrate nel periodo.'
                      : 'Non hai ancora registrato nessuna spesa.'}
                  </div>
                </>
              ) : (
                <PremiumFeatureLock compact onUpgrade={goToUpgrade}>
                  <div className={styles.finItemHeader}>
                    <div className={styles.label}>Uscite</div>
                    <button type="button" className={styles.vediSpese} tabIndex={-1}>
                      Vedi spese <span className={styles.vediSpeseChev}>&gt;</span>
                    </button>
                  </div>
                  <div className={styles.finValueRow}>
                    <div className={clsx(styles.value, styles.mono)}>
                      {formatEuro(MOCK_USCITE.amount)}
                    </div>
                    <button type="button" className={styles.finDettaglio} tabIndex={-1}>
                      Dettaglio <span className={styles.finDettaglioChev}>▾</span>
                    </button>
                  </div>
                  <DeltaBadge
                    delta={MOCK_USCITE.delta}
                    deltaLabel={MOCK_USCITE.deltaLabel}
                    positiveIsDown
                  />
                  <div className={styles.metricHint}>{MOCK_USCITE.hint}</div>
                </PremiumFeatureLock>
              )}
            </div>
            <div
              className={clsx(
                styles.finItem,
                styles.utile,
                !isPremiumUnlocked && styles.finItemLocked,
              )}
            >
              {isPremiumUnlocked ? (
                <>
                  <div className={styles.label}>Utile</div>
                  <div
                    className={clsx(
                      styles.value,
                      styles.mono,
                      utile >= 100 ? styles.utileGood : utile >= 50 ? styles.utileWarn : styles.utileBad,
                    )}
                  >
                    {formatEuro(utile)}
                  </div>
                  <DeltaBadge
                    delta={scope.deltas?.utile}
                    deltaLabel={scope.deltaLabel}
                    refreshing={compareRefreshing}
                  />
                  <div className={styles.metricHint}>
                    {usciteTotale > 0
                      ? 'Differenza tra entrate e uscite.'
                      : 'Senza spese registrate resta tutto: inseriscile per un utile vero.'}
                  </div>
                </>
              ) : (
                <PremiumFeatureLock compact onUpgrade={goToUpgrade}>
                  <div className={styles.label}>Utile</div>
                  <div className={clsx(styles.value, styles.mono, styles.utileGood)}>
                    {formatEuro(MOCK_UTILE.amount)}
                  </div>
                  <DeltaBadge delta={MOCK_UTILE.delta} deltaLabel={MOCK_UTILE.deltaLabel} />
                  <div className={styles.metricHint}>{MOCK_UTILE.hint}</div>
                </PremiumFeatureLock>
              )}
            </div>
          </>
        )}
      </SoftLoad>

      <div className={styles.analisiBox}>
        <div className={styles.analisiHeader}>
          <h2 className={styles.sectionTitle}>Prenotazioni e servizi</h2>
        </div>

        <SoftLoad loading={softSectionLoading.finance} className={styles.fatturatoDashboard}>
          {hardSectionLoading.finance ? (
            <div className={styles.sectionLoaderSpan}>
              <SectionSkeleton variant="metrics" />
            </div>
          ) : (
            <>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Fatturato</span>
            </div>
            <div className={styles.metricValueRow}>
              <span className={clsx(styles.value, styles.mono)}>{formatEuro(fatturatoAnalisi)}</span>
              {fatturatoDelta ? (
                <DeltaBadge
                  delta={fatturatoDelta}
                  deltaLabel={scope.deltaLabel}
                  refreshing={compareRefreshing}
                />
              ) : null}
            </div>
            {!fatturatoDelta ? (
              <div className={styles.metricHint}>Nessun periodo di confronto selezionato.</div>
            ) : null}
          </div>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Scontrino medio</span>
            </div>
            <div className={styles.metricValueRow}>
              <span className={clsx(styles.value, styles.mono)}>{formatEuro(scontrinoMedio)}</span>
              {scontrinoMedioDelta ? (
                <DeltaBadge
                  delta={scontrinoMedioDelta}
                  deltaLabel={scope.deltaLabel}
                  refreshing={compareRefreshing}
                />
              ) : null}
            </div>
            <div className={styles.metricHint}>
              Spesa media per prenotazione, su {prenotazioniAnalisi} prenotazioni.
            </div>
          </div>
            </>
          )}
        </SoftLoad>

        <SoftLoad loading={softSectionLoading.bookings} className={styles.cardsRow}>
          {hardSectionLoading.bookings ? (
            <div className={styles.sectionLoaderSpan}>
              <SectionSkeleton variant="bookings" />
            </div>
          ) : (
            <>
          <div className={styles.panelCol}>
            <div className={styles.cardTitleRow}>
              <h3 className={styles.cardTitle}>Prenotazioni</h3>
              {showPoltronaSplit ? (
                <DetailButton onClick={() => setPoltronaModal('canali')} />
              ) : null}
            </div>
            <div className={clsx(styles.cardTitleMeta, styles.mono)}>
              {canaliTotal.toLocaleString('it-IT')} totali *
            </div>
            <CategoryBars items={scope.canali} deltas={scope.canaliDeltas} />
            <div className={styles.categoryFootnote}>* escluse prenotazioni ricorrenti</div>
          </div>

          <div className={styles.panelCol}>
            <div className={styles.cardTitleRow}>
              <h3 className={styles.cardTitle}>Servizi prenotati</h3>
              {showPoltronaSplit ? (
                <DetailButton onClick={() => setPoltronaModal('servizi')} />
              ) : null}
            </div>
            <div className={clsx(styles.cardTitleMeta, styles.mono)}>
              {serviziTotal.toLocaleString('it-IT')} totali
            </div>
            <CategoryBars items={scope.servizi} deltas={scope.serviziDeltas} />
          </div>
            </>
          )}
        </SoftLoad>

        <SoftLoad loading={softSectionLoading.bookings} className={styles.ricorrentiBox}>
        {hardSectionLoading.bookings ? (
          <div className={styles.sectionLoaderSpan}>
            <SectionSkeleton variant="card" />
          </div>
        ) : (
          <>
        <div className={styles.ricorrentiMain}>
          <h2 className={styles.sectionTitle}>Prenotazioni ricorrenti</h2>
          <div className={styles.metricValueRow}>
            <div className={clsx(styles.ricorrentiTotal, styles.mono)}>{ricTotale}</div>
            <DeltaBadge
              delta={scope.ricorrentiDeltas?.totale}
              deltaLabel={scope.deltaLabel}
              refreshing={compareRefreshing}
            />
            <span className={styles.ricorrentiShareLabel}>
              {formatDecimal(ricSharePct, 1)}% di tutte le prenotazioni
            </span>
          </div>
          <div className={styles.ricShareTrack}>
            <span
              className={styles.ricShareFill}
              style={{ width: `${Math.min(100, ricSharePct)}%` }}
            />
          </div>
          <div className={styles.ricorrentiStats}>
            <div>
              <div className={styles.label}>Valore stimato</div>
              <div className={styles.metricValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>{formatEuro(ricValue)}</span>
                <span className={styles.metricHint}>Allo scontrino medio del periodo.</span>
              </div>
            </div>
            <div>
              <div className={styles.label}>Al mese</div>
              <div className={styles.metricValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>{ricMonthly}</span>
                <span className={styles.metricHint}>
                  Appuntamenti ricorrenti in agenda ogni mese.
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.ricorrentiTeam}>
          <h3 className={styles.cardTitle}>Distribuzione nel team</h3>
          <div className={styles.metricHint}>Quota delle ricorrenti gestite</div>
          <ul className={styles.ricorrentiList}>
            {(scope.ricorrenti?.team || []).map((member) => {
              const memberId = String(member.id);
              const color = getPoltronaColor(memberId, scope.team);
              const isMuted = poltrona !== 'tutte' && poltrona !== memberId;
              const shortName = (member.name || '')
                .split(' ')
                .filter(Boolean)
                .map((part, idx) => (idx === 0 ? part : `${part[0]}.`))
                .join(' ');
              return (
                <li
                  key={memberId}
                  className={clsx(styles.ricorrentiRow, { [styles.ricorrentiRowMuted]: isMuted })}
                >
                  <span className={styles.ricorrentiName}>
                    <i className={styles.poltronaDot} style={{ background: color }} />
                    {shortName}
                  </span>
                  <div className={styles.ricorrentiBarTrack}>
                    <span
                      className={styles.ricorrentiBarFill}
                      style={{ width: `${member.pct || 0}%`, background: color }}
                    />
                  </div>
                  <span className={clsx(styles.ricorrentiPct, styles.mono)}>{member.pct || 0}%</span>
                </li>
              );
            })}
          </ul>
        </div>
          </>
        )}
      </SoftLoad>
      </div>

      <div className={styles.queueDisdetteRow}>
      <SoftLoad loading={softSectionLoading.queue} className={styles.queueCard}>
        {hardSectionLoading.queue ? (
          <SectionSkeleton variant="queue" />
        ) : (
          <>
            <div className={styles.queueCardHead}>
              <div>
                <h2 className={styles.sectionTitle}>Lista d&apos;attesa</h2>
                <div className={styles.metricHint}>
                  Clienti che hanno chiesto un posto e quanti sono davvero riusciti a sedersi.
                </div>
              </div>
              {!isMobile ? (
                <div className={styles.queueCardHeadRight}>
                  <DetailButton onClick={() => setPoltronaModal('queue')} />
                </div>
              ) : null}
            </div>
          <div className={styles.queueStatsRow}>
            <div>
              <div className={styles.label}>In attesa</div>
              <div className={styles.queueStatValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>{codaSum}</span>
                <span className={styles.metricHint}>clienti nel periodo</span>
              </div>
            </div>
            <div>
              <div className={styles.label}>Recuperati</div>
              <div className={styles.queueStatValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>{recSum}</span>
                <span className={styles.metricHint}>
                  {codaSum > 0
                    ? `${formatDecimal(safeDivide(recSum, codaSum) * 100, 1)}% della coda`
                    : '—'}
                </span>
              </div>
            </div>
            <div>
              <div className={styles.label}>{queueAvgCopy.avg}</div>
              <div className={styles.queueStatValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>
                  {formatDecimal(safeDivide(codaSum, Math.max(1, (scope.queue || []).length)), 1)}
                </span>
                <span className={styles.metricHint}>clienti in attesa</span>
              </div>
            </div>
            <div>
              <div className={styles.label}>Picco</div>
              <div className={styles.queueStatValueRow}>
                <span className={clsx(styles.valueSm, styles.mono)}>
                  {(scope.queue || []).reduce(
                    (best, row) => (row.queue > (best?.queue || 0) ? row : best),
                    null,
                  )?.label || '—'}
                </span>
                <span className={styles.metricHint}>{`${queueMax} in attesa`}</span>
              </div>
            </div>
          </div>
          <QueueChart
            queue={scope.queue}
            queueMax={queueMax}
            recoveredMax={recoveredMax}
            hoveredIndex={queueHoverIndex}
            onHover={setQueueHoverIndex}
            groupBy={queueGroupBy}
            isMobile={isMobile}
            detailAction={
              isMobile ? <DetailButton onClick={() => setPoltronaModal('queue')} /> : null
            }
          />
          <div
            className={clsx(styles.waitlistHoverMeta, styles.waitlistHoverMetaFooter, {
              [styles.active]: !!hoveredQueueRow,
            })}
          >
            {hoveredQueueRow ? formatWaitlistHover(hoveredQueueRow) : '\u00a0'}
          </div>
          <div className={styles.waitlistMissedFooter}>
            <strong>
              {Math.max(0, codaSum - recSum).toLocaleString('it-IT')} clienti
            </strong>{' '}
            sono rimasti in lista d&apos;attesa senza mai entrare in agenda: circa{' '}
            {formatEuro(Math.round(Math.max(0, codaSum - recSum) * (Number(scontrinoMedio) || 0)))}{' '}
            di incasso non realizzato allo scontrino medio.
          </div>
          </>
        )}
      </SoftLoad>

      <SoftLoad loading={softSectionLoading.bookings} className={styles.disdetteCard}>
        {hardSectionLoading.bookings ? (
          <SectionSkeleton variant="card" />
        ) : (
          <>
          <h2 className={styles.sectionTitle}>Disdette</h2>
          <div className={styles.metricHint}>
            {scope.disdette.totale} appuntamenti saltati
            {scope.disdette.totale > 0
              ? `: ${formatDecimal(
                  (scope.disdette.daCliente / scope.disdette.totale) * 100,
                  1,
                )}% annullati dal cliente, ${formatDecimal(
                  (scope.disdette.daSalone / scope.disdette.totale) * 100,
                  1,
                )}% dal salone.`
              : '.'}
          </div>
          <div className={styles.waffleGrid}>
            {[
              {
                key: 'cliente',
                title: 'Chi disdice: il cliente',
                rate: disClienteRate,
                count: scope.disdette.daCliente,
                color: COLOR_MAP.disdetteCliente,
                share:
                  scope.disdette.totale > 0
                    ? `${formatDecimal(
                        (scope.disdette.daCliente / scope.disdette.totale) * 100,
                        1,
                      )}% delle disdette`
                    : '—',
                note: 'Non si presenta o annulla la prenotazione che ha preso.',
                delta: scope.disdetteDeltas?.daCliente,
              },
              {
                key: 'salone',
                title: 'Chi disdice: il salone',
                rate: disSaloneRate,
                count: scope.disdette.daSalone,
                color: COLOR_MAP.disdetteSalone,
                share:
                  scope.disdette.totale > 0
                    ? `${formatDecimal(
                        (scope.disdette.daSalone / scope.disdette.totale) * 100,
                        1,
                      )}% delle disdette`
                    : '—',
                note: 'Appuntamenti cancellati da voi: buchi in agenda che il cliente non ha scelto.',
                delta: scope.disdetteDeltas?.daSalone,
              },
            ].map((block) => (
              <div key={block.key} className={styles.waffleBlock}>
                <div className={styles.waffleBlockTitle}>
                  <i className={styles.poltronaDot} style={{ background: block.color }} />
                  {block.title}
                </div>
                <div className={styles.waffleBlockTop}>
                  <div className={styles.waffleRateWrap}>
                    <span
                      className={clsx(styles.waffleRate, styles.mono)}
                      style={{ color: block.color }}
                    >
                      {formatDecimal(block.rate, 1)}
                    </span>
                    <span className={styles.waffleRatePct}>%</span>
                    <span className={styles.waffleRateLabel}>
                      delle
                      <br />
                      prenotazioni
                    </span>
                  </div>
                  <div className={styles.waffleCountWrap}>
                    <div className={clsx(styles.valueSm, styles.mono)}>
                      {Number(block.count || 0).toLocaleString('it-IT')}
                    </div>
                    <div className={styles.metricHint}>{block.share}</div>
                  </div>
                </div>
                <WaffleGrid ratePct={block.rate} color={block.color} />
                <div className={styles.metricHint}>
                  In media, su 100 prenotazioni del periodo,{' '}
                  <strong style={{ color: block.color }}>{Math.round(block.rate)}</strong> sono
                  disdette così. {block.note}
                  <InlineDelta delta={block.delta} positiveIsDown />
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </SoftLoad>
      </div>

      <SoftLoad
        loading={softSectionLoading.finance}
        className={clsx(styles.teamBox, !isPremiumUnlocked && styles.teamBoxLocked)}
      >
        {hardSectionLoading.finance ? (
          <SectionSkeleton variant="team" />
        ) : isPremiumUnlocked ? (
          <>
        <div className={styles.teamHeader}>
          <h2 className={styles.sectionTitle}>Chi lavora di più</h2>
          <span className={styles.metricHint}>
            Dal più al meno incasso · clicca una scheda per vedere solo i suoi numeri
          </span>
        </div>
        <div className={styles.teamCarousel} ref={teamCarouselRef}>
          <div className={styles.teamTrack}>
            {teamRanked.map((member) => {
              const memberId = String(member.id);
              const teamColor = getPoltronaColor(memberId, scope.team);

              return (
                <button
                  type="button"
                  key={memberId}
                  ref={(el) => {
                    teamCardRefs.current[memberId] = el;
                  }}
                  className={clsx(styles.teamCard, {
                    [styles.teamCardSelected]: poltrona === memberId,
                    [styles.teamCardMuted]: poltrona !== 'tutte' && poltrona !== memberId,
                  })}
                  style={
                    poltrona === memberId
                      ? {
                          '--team-accent': teamColor,
                          '--team-accent-soft': poltronaColorWithAlpha(teamColor, 0.14),
                          '--team-accent-border': poltronaColorWithAlpha(teamColor, 0.32),
                        }
                      : undefined
                  }
                  onClick={() => setPoltrona(poltrona === memberId ? 'tutte' : memberId)}
                >
                  <div className={styles.teamCardHeader}>
                    <div className={styles.teamAvatar} style={{ '--team-accent': teamColor }}>
                      {getInitials(member.name)}
                    </div>
                    <div className={styles.teamIdentity}>
                      <div className={styles.teamName}>{member.name}</div>
                    </div>
                    <span
                      className={clsx(styles.teamRank, member.rank === 1 && styles.teamRankFirst)}
                    >
                      {member.rank}°
                    </span>
                  </div>
                  <div className={styles.teamStatsDashboard}>
                    <div className={styles.teamStatSection}>
                      <span className={styles.teamStatLabel}>Incassato</span>
                      <span className={clsx(styles.teamStatValue, styles.mono)}>
                        {formatEuro(member.fatturato)}
                      </span>
                    </div>
                    <div className={styles.teamStatSection}>
                      <span className={styles.teamStatLabel}>Prenotazioni</span>
                      <span className={clsx(styles.teamStatValue, styles.mono)}>
                        {(member.prenotazioni || 0).toLocaleString('it-IT')}
                      </span>
                    </div>
                  </div>
                  <div className={styles.teamMeta}>
                    <div>{formatDecimal(member.sharePct, 1)}% dell&apos;incasso</div>
                    <div>{formatEuro(member.avg)} a prenotazione</div>
                    <div>{member.gapNote}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
          </>
        ) : (
          <PremiumFeatureLock onUpgrade={goToUpgrade}>
            <div className={styles.premiumLockTeamMock}>
            <div className={styles.teamHeader}>
              <h2 className={styles.sectionTitle}>Chi lavora di più</h2>
              <span className={styles.metricHint}>
                Dal più al meno incasso · clicca una scheda per vedere solo i suoi numeri
              </span>
            </div>
            <div className={styles.teamCarousel}>
              <div className={styles.teamTrack}>
                {lockedTeamPreview.map((member) => (
                  <div
                    key={member.id}
                    className={styles.teamCard}
                    style={{
                      '--team-accent': member.color,
                      '--team-accent-soft': poltronaColorWithAlpha(member.color, 0.14),
                      '--team-accent-border': poltronaColorWithAlpha(member.color, 0.32),
                    }}
                  >
                    <div className={styles.teamCardHeader}>
                      <div
                        className={styles.teamAvatar}
                        style={{ '--team-accent': member.color }}
                      >
                        {getInitials(member.name)}
                      </div>
                      <div className={styles.teamIdentity}>
                        <div className={styles.teamName}>{member.name}</div>
                      </div>
                      <span
                        className={clsx(
                          styles.teamRank,
                          member.rank === 1 && styles.teamRankFirst,
                        )}
                      >
                        {member.rank}°
                      </span>
                    </div>
                    <div className={styles.teamStatsDashboard}>
                      <div className={styles.teamStatSection}>
                        <span className={styles.teamStatLabel}>Incassato</span>
                        <span className={clsx(styles.teamStatValue, styles.mono)}>
                          {formatEuro(member.fatturato)}
                        </span>
                      </div>
                      <div className={styles.teamStatSection}>
                        <span className={styles.teamStatLabel}>Prenotazioni</span>
                        <span className={clsx(styles.teamStatValue, styles.mono)}>
                          {member.prenotazioni.toLocaleString('it-IT')}
                        </span>
                      </div>
                    </div>
                    <div className={styles.teamMeta}>
                      <div>{formatDecimal(member.sharePct, 1)}% dell&apos;incasso</div>
                      <div>{formatEuro(member.avg)} a prenotazione</div>
                      <div>{member.gapNote}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
          </PremiumFeatureLock>
        )}
      </SoftLoad>

      <SoftLoad loading={softSectionLoading.clients} className={styles.clientiBox}>
        {hardSectionLoading.clients ? (
          <SectionSkeleton variant="clients" />
        ) : (
          <>
        <div className={styles.clientiHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Clienti</h2>
            <div className={styles.metricHint}>
              Chi è passato dal salone nel periodo e quanto spesso torna.
            </div>
          </div>
        </div>

        <div className={styles.clientiRegistrati}>
          <div className={styles.clientiRegistratiMain}>
            <div className={styles.label}>Clienti registrati</div>
            <div className={clsx(styles.clientiRegistratiValue, styles.mono)}>
              {clientiTotali.toLocaleString('it-IT')}
            </div>
            <div className={styles.metricHint}>
              {clientiAttiviPeriodo.toLocaleString('it-IT')} hanno prenotato nel periodo ·{' '}
              {formatDecimal(clientiAttiviPct, 1)}%
            </div>
          </div>
          <div className={styles.clientiRegistratiChart}>
            <div className={styles.clientiRegistratiTrack}>
              <i
                className={styles.clientiRegistratiSegRecenti}
                style={{ width: clientiBarPct(clientiRecenti) }}
              />
              <i
                className={styles.clientiRegistratiSegRischio}
                style={{ width: clientiBarPct(clientiRischioBar) }}
              />
              <i
                className={styles.clientiRegistratiSegDormienti}
                style={{ width: clientiBarPct(clientiDormienti) }}
              />
            </div>
            <div className={styles.clientiRegistratiLegend}>
              <span>
                <i className={styles.clientiRegistratiDotRecenti} />
                {clientiRecenti.toLocaleString('it-IT')} {clientiRecentiLabel}
              </span>
              <span>
                <i className={styles.clientiRegistratiDotRischio} />
                {clientiRischioBar.toLocaleString('it-IT')} fermi da più di un mese
              </span>
              <span>
                <i className={styles.clientiRegistratiDotDormienti} />
                {clientiDormienti.toLocaleString('it-IT')} altri registrati
              </span>
            </div>
          </div>
        </div>

        <div className={styles.giorniChart}>
          <h3 className={styles.cardTitle}>
            Prenotazioni per giorno
            {bestDay ? (
              <span className={styles.giorniBest}>
                {' '}
                · il più pieno è {bestDay.label} con {bestDay.count}
              </span>
            ) : null}
          </h3>
          <div className={styles.giorniBars}>
            {(clientiScope.giorni || []).map((d) => {
              const barClass =
                d.count === giorniMax ? styles.max : d.count === giorniMin ? styles.min : '';
              const valueClass =
                barClass === styles.min
                  ? styles.giorniBarValueMin
                  : barClass === styles.max
                    ? styles.giorniBarValueMax
                    : styles.giorniBarValueMuted;
              const closed = d.count === 0;

              return (
                <div key={d.label} className={styles.col}>
                  <div className={styles.giorniColInner}>
                    <div
                      className={clsx(styles.giorniBar, barClass)}
                      style={{
                        height: closed ? '3px' : `${((d.count / giorniMax) * 100).toFixed(0)}%`,
                      }}
                    >
                      <span className={clsx(styles.giorniBarValue, valueClass)}>
                        {closed ? 'chiuso' : d.count}
                      </span>
                    </div>
                  </div>
                  <div className={styles.dayLabel}>{d.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={clsx(styles.fatturatoDashboard, styles.clientiMetricsDashboard)}>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Clienti prenotati</span>
            </div>
            <div className={clsx(styles.value, styles.mono)}>{clientiScope.clientiAttivi}</div>
            <div className={styles.metricHint}>
              Hanno prenotato almeno una volta nel periodo.
            </div>
          </div>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Clienti che stai perdendo</span>
            </div>
            <div className={clsx(styles.value, styles.mono)}>
              {clientiScope.clientiRischioAbbandono}
            </div>
            <div className={styles.metricHint}>Non li vedi da più di un mese.</div>
          </div>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Clienti che disdicono spesso</span>
            </div>
            <div className={clsx(styles.value, styles.mono, styles.valueDanger)}>
              {formatDecimal(clientiPericolosiPct, 1)}%
            </div>
            <div className={styles.metricHint}>Disdicono spesso i loro appuntamenti.</div>
          </div>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Quante volte tornano</span>
            </div>
            <div className={clsx(styles.value, styles.mono)}>{formatDecimal(frequenzaVisita)}</div>
            <div className={styles.metricHint}>Visite medie per cliente nel periodo.</div>
          </div>
          <div className={styles.fatturatoSection}>
            <div className={styles.labelRow}>
              <span className={styles.label}>Quanto lascia un cliente</span>
            </div>
            <div className={clsx(styles.value, styles.mono)}>
              {formatEuro(scontrinoMedioCliente)}
            </div>
            <div className={styles.metricHint}>Somma di tutte le sue visite.</div>
          </div>
        </div>
          </>
        )}
      </SoftLoad>

      <div className={styles.reviewsBox}>
        <div className={styles.reviewsHeader}>
          <h2 className={styles.sectionTitle}>Recensioni</h2>
          <button type="button" className={styles.vediSpese} onClick={goToReviews}>
            Vedi tutte le recensioni →
          </button>
        </div>
        <ReviewsStatsCard reviewsStats={reviewsStats} isMobile={isMobile} />
      </div>
      </div>

      <Modal
        isModalOpen={finModal === 'entrate'}
        setIsModalOpen={(open) => !open && setFinModal(null)}
        centered
        showCloseBtn
        title="Dettaglio entrate"
        subTitle=""
        width="520px"
        maxWidth="92vw"
        maxHeight="90vh"
      >
        <div className={styles.finModalContent}>
          <FinBreakdownBars
            items={entrateBreakdown}
            totalLabel="Fatturato per poltrona"
            accentColor={FIN_MODAL_ACCENT.entrate}
          />
        </div>
      </Modal>

      {isPremiumUnlocked ? (
        <Modal
          isModalOpen={finModal === 'uscite'}
          setIsModalOpen={(open) => !open && setFinModal(null)}
          centered
          showCloseBtn
          title="Dettaglio uscite"
          subTitle=""
          width="520px"
          maxWidth="92vw"
          maxHeight="90vh"
        >
          <div className={styles.finModalContent}>
            <FinBreakdownBars
              items={scope.finUsciteBreakdown}
              totalLabel="Uscite per tipologia"
              accentColor={FIN_MODAL_ACCENT.uscite}
            />
          </div>
        </Modal>
      ) : null}

      <Modal
        isModalOpen={!!poltronaModal}
        setIsModalOpen={(open) => !open && setPoltronaModal(null)}
        centered
        showCloseBtn
        title={poltronaModalTable?.title || 'Dettaglio poltrone'}
        subTitle=""
        width={isMobile ? '100%' : '720px'}
        maxWidth={isMobile ? 'calc(100vw - 24px)' : '92vw'}
        maxHeight={isMobile ? 'min(88vh, 88dvh)' : '90vh'}
      >
        <div className={styles.finModalContent}>
          {poltronaModalTable && (
            <PoltronaDetailTable
              columns={poltronaModalTable.columns}
              rows={poltronaModalTable.rows}
              cells={poltronaModalTable.cells}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
export default StatisticNew;
